/**
 * ScreenGrid: react-grid-layout wrapper showing 1 ScreenTile per employee.
 */
import { useMemo, useState } from 'react'
import { Responsive, WidthProvider, type Layout } from 'react-grid-layout'
import 'react-grid-layout/css/styles.css'
import 'react-resizable/css/styles.css'
import type { Employee } from '@monitor/shared'
import { ScreenTile } from './screen-tile'

const ResponsiveGridLayout = WidthProvider(Responsive)

interface ScreenGridProps {
  employees: Employee[]
  onSelect: (employee: Employee) => void
}

export function ScreenGrid({ employees, onSelect }: ScreenGridProps) {
  // Generate layout. Each tile = 16:9 screenshot area + ~30px info card.
  // rowHeight=80, h=3 gives image area = ~195px (matches 16:9 at tileWidth≈347px)
  //   - Image area: aspect-video in screen-tile.tsx
  //   - Info card: ~30px fixed
  //   - 3 tiles per row on desktop (12/4), 2 on tablet (6/3), 1 mobile
  const layouts = useMemo(() => {
    const desktop: Layout[] = employees.map((e, i) => ({
      i: String(e.id),
      x: (i * 4) % 12,
      y: Math.floor((i * 4) / 12) * 3,
      w: 4,
      h: 3,
      minW: 3,
      minH: 3,
    }))
    const tablet: Layout[] = employees.map((e, i) => ({
      i: String(e.id),
      x: (i * 3) % 6,
      y: Math.floor((i * 3) / 6) * 3,
      w: 3,
      h: 3,
      minW: 2,
      minH: 3,
    }))
    const mobile: Layout[] = employees.map((e, i) => ({
      i: String(e.id),
      x: 0,
      y: i * 3,
      w: 2,
      h: 3,
      minW: 1,
      minH: 3,
    }))
    return { lg: desktop, md: tablet, sm: mobile }
  }, [employees])

  // Allow user to lock/unlock drag
  const [draggable, setDraggable] = useState(false)

  if (employees.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed text-sm text-muted-foreground">
        Không có nhân viên nào. Thêm nhân viên ở tab "Nhân viên".
      </div>
    )
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-end">
        <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={draggable}
            onChange={(e) => setDraggable(e.target.checked)}
            className="h-3.5 w-3.5"
          />
          Cho phép sắp xếp lại
        </label>
      </div>
      <ResponsiveGridLayout
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
        cols={{ lg: 12, md: 6, sm: 2, xs: 1, xxs: 1 }}
        rowHeight={80}
        isDraggable={draggable}
        isResizable={false}
        margin={[8, 8]}
        containerPadding={[0, 0]}
        compactType="vertical"
      >
        {employees.map((emp) => (
          <div key={emp.id}>
            <ScreenTile employee={emp} onClick={() => onSelect(emp)} />
          </div>
        ))}
      </ResponsiveGridLayout>
    </div>
  )
}
